import cx from "classnames"
import { useState, useEffect } from "react"
import { useRange, useRefinementList } from "react-instantsearch-hooks-web"
import { useWorkbenchStore } from "../../store/store"
import { MAX_RELEASE_YEAR, MIN_RELEASE_YEAR, transformFlags } from "./FilterTransforms"
import { useToggleRefinement } from "react-instantsearch-hooks-web"
import CircleRange from "./CircleRange"

const InternalToggleRefinement = ({
  label,
  value,
  refineOn,
  refineOff,
  selection,
  setSelection,
}) => {
  const handleClick = () => {
    if (selection == "") {
      setSelection("selected")
      refineOn({ isRefined: selection === "selected" })
    } else if (selection === "selected") {
      setSelection("selected-negative")
      refineOn({ isRefined: selection === "selected" })
      refineOff({ isRefined: selection === "selected-negative" })
    } else {
      // "selected-negative"
      setSelection("")
      refineOff({ isRefined: selection === "selected-negative" })
    }
  }

  return (
    <li className={cx("actionable", selection)}>
      {" "}
      {/* selected, selected-negative */}
      <a onClick={handleClick}>{label}</a>
    </li>
  )
}

const FlagRefinementListItem = ({ item, refine }) => {
  const flagsToggledOn = useWorkbenchStore((state) => state.flagsToggledOn)
  const addToFlagsOn = useWorkbenchStore((state) => state.addToFlagsOn)
  const removeFromFlagsOn = useWorkbenchStore((state) => state.removeFromFlagsOn)

  const handleClick = (e) => {
    e.preventDefault()
    if (!flagsToggledOn.includes(item.value)) {
      addToFlagsOn(item.value)
      refine(item.value)
    } else {
      removeFromFlagsOn(item.value)
      refine(item.value)
    }
  }

  return (
    <li
      data-val={item.label}
      data-cy={`internal-option-${item.value}`}
      data-pretty={`${item.label} Flag`}
      className={cx(
        "actionable",
        item.label.toLowerCase(),
        flagsToggledOn.includes(item.value) ? "selected" : ""
      )}
    >
      {" "}
      <a onClick={handleClick}>{item.label}</a>
    </li>
  )
}

const FlagsRefinementList = (props) => {
  const { items, refine } = useRefinementList(props)

  //For toggleable
  const { value: valueHasStemsOn, refine: refineHasStemsOn } = useToggleRefinement({
    attribute: "is_has_stems",
  })
  const { refine: refineHasStemsOff } = useToggleRefinement({ attribute: "is_not_has_stems" })
  const hasStemsSelection = useWorkbenchStore((state) => state.hasStemsSelection)
  const setHasStemsSelection = useWorkbenchStore((state) => state.setHasStemsSelection)

  const { value: valueIsInTrackClubOn, refine: refineIsInTrackClubOn } = useToggleRefinement({
    attribute: "is_in_track_club",
  })
  const { refine: refineIsInTrackClubOff } = useToggleRefinement({
    attribute: "is_not_in_track_club",
  })
  const isInTrackClubSelection = useWorkbenchStore((state) => state.isInTrackClubSelection)
  const setIsInTrackClubSelection = useWorkbenchStore((state) => state.setIsInTrackClubSelection)

  return (
    <div className="toggleable-container">
      <ul>
        {items.map((item, index) => (
          <FlagRefinementListItem key={index} item={item} refine={refine} />
        ))}
      </ul>

      <ol>
        <InternalToggleRefinement
          label="Has Stems"
          value={valueHasStemsOn}
          refineOn={refineHasStemsOn}
          refineOff={refineHasStemsOff}
          selection={hasStemsSelection}
          setSelection={setHasStemsSelection}
        />
        <InternalToggleRefinement
          label="Track Club"
          value={valueIsInTrackClubOn}
          refineOn={refineIsInTrackClubOn}
          refineOff={refineIsInTrackClubOff}
          selection={isInTrackClubSelection}
          setSelection={setIsInTrackClubSelection}
        />
      </ol>
    </div>
  )
}

const ReleaseYearRangeFilter = () => {
  const [value, setValue] = useState({ min: MIN_RELEASE_YEAR, max: MAX_RELEASE_YEAR })
  const currentFilter = useWorkbenchStore((state) => state.currentFilter)
  const releaseYearMin = useWorkbenchStore((state) => state.releaseYearMin)
  const releaseYearMax = useWorkbenchStore((state) => state.releaseYearMax)
  const setReleaseYearMin = useWorkbenchStore((state) => state.setReleaseYearMin)
  const setReleaseYearMax = useWorkbenchStore((state) => state.setReleaseYearMax)
  const { refine } = useRange({
    attribute: "release_year",
    max: MAX_RELEASE_YEAR,
  })

  const handleChange = (values) => {
    setValue(values)
  }

  const handleDragEnd = (minValue, maxValue) => {
    // Below code allows for dragging either end back to undo refining by year
    // So that songs without release_years can populate the page
    minValue = minValue > MIN_RELEASE_YEAR || maxValue < MAX_RELEASE_YEAR ? minValue : undefined
    maxValue = maxValue < MAX_RELEASE_YEAR ? maxValue : undefined
    setReleaseYearMin(minValue)
    setReleaseYearMax(maxValue)
    refine([minValue, maxValue])
  }

  useEffect(() => {
    if (currentFilter === "internal") {
      setValue({ min: releaseYearMin || MIN_RELEASE_YEAR, max: releaseYearMax || MAX_RELEASE_YEAR })
    }
  }, [currentFilter])

  useEffect(() => {
    // MIN val has been set to undefined or 0 but MAX is still a defined number
    // Special case: since max is still defined while min isn't, we can't allow
    // algolia to show songs with release_year equal to 0, so we use MIN_RELEASE_YEAR instead
    if (!(releaseYearMin > MIN_RELEASE_YEAR) && releaseYearMax) {
      setValue({ min: MIN_RELEASE_YEAR, max: releaseYearMax })
      refine([MIN_RELEASE_YEAR, releaseYearMax])
      return
    }
    // MAX val has been set to undefined but MIN is still a defined number > MIN_RELEASE_YEAR
    else if (
      (!releaseYearMax || releaseYearMax === MAX_RELEASE_YEAR) &&
      releaseYearMin > MIN_RELEASE_YEAR
    ) {
      setValue((curr) => ({ ...curr, max: MAX_RELEASE_YEAR }))
    }

    // Both Values have been set to undefined in workbench state so display them at their respective extremes
    // This is a special case as it needs to not refine by either end of the range
    if (
      !(releaseYearMin > MIN_RELEASE_YEAR) &&
      (releaseYearMax == undefined || releaseYearMax === MAX_RELEASE_YEAR)
    ) {
      setValue({ min: MIN_RELEASE_YEAR, max: MAX_RELEASE_YEAR })
      refine([undefined, undefined])
      return
    }

    refine([releaseYearMin, releaseYearMax])
  }, [releaseYearMin, releaseYearMax])

  return (
    <div className="mm-filter-release-year">
      <CircleRange
        attribute="release_year"
        min={MIN_RELEASE_YEAR}
        max={MAX_RELEASE_YEAR}
        step={1}
        value={value}
        onChange={handleChange}
        onChangeEnd={handleDragEnd}
        convertToMinutes={false}
        minStepDifference={0}
      />
    </div>
  )
}

const WorkbenchFilterInternalBeta = ({ id }) => {
  const currentFilter = useWorkbenchStore((state) => state.currentFilter)

  return (
    <fieldset id={id} className={cx("mm-filter", currentFilter === id ? "current" : "")}>
      <FlagsRefinementList
        attribute="internal_flags"
        operator="or"
        transformItems={transformFlags}
      />
      <ReleaseYearRangeFilter />
    </fieldset>
  )
}

export default WorkbenchFilterInternalBeta
